import React, { FunctionComponent, useState, useEffect } from "react";
import WizardContainer, { Invocations, SelectedWidget } from "../../containers/Wizard";
import OrigamiPreview from "../OrigamiPreview";
import { dateNormalizer } from "../../shared/normalizers";
import { Tab } from "@own-local/ad-unit/lib/AdUnit";
import OrderSummary, { ItemProps } from "../OrderSummary";
import { PaymentDetailsProps } from "../../containers/Wizard/actions";
import { isUrlValid } from "../../shared/validations"
import { Spinner } from "../Spinner";

export const ORIGAMI_DATA_SPELLS = [
  "business",
  "business_name",
  "caption",
  "background",
  "logo",
  "layout",
  "address",
  "city",
  "zip",
  "business_phone",
  "website",
  "facebook",
  "twitter",
  "instagram",
  "additional_text",
  "email",
  "state",
];

const ORIGAMI_DATA_PREPENDS_MAP: OrigamiDataType = {
  facebook: "https://facebook.com/",
  twitter: "https://twitter.com/",
  instagram: "https://instagram.com/",
};

export type OrigamiDataType = {
  [key: string]: string | object | undefined;
};

type OrderConfirmationStepProps = {
  invocations: Invocations;
  changePath: Function;
  fetchPaymentDetails: Function;
  paymentDetails: PaymentDetailsProps;
};

const defineOrigamiData = (invocations: Invocations): OrigamiDataType => {
  return ORIGAMI_DATA_SPELLS.reduce<OrigamiDataType>(
    (origamiData, spell): OrigamiDataType => {
      const prependValue = ORIGAMI_DATA_PREPENDS_MAP[spell] || "";
      if (invocations[spell]) origamiData[spell] = `${prependValue}${invocations[spell]}`;

      if (spell === "logo") {
        origamiData["logo"] = isUrlValid(invocations["logo"]?.toString()) ? invocations["logo"] : ""
      }

      return origamiData;
    },
    {},
  );
};

const calculateEndDate = (startDate: string, weeks: number) => {
  const totalTime = 1000 * 60 * 60 * 24 * 7 * weeks;
  const endDate = new Date(
    new Date(startDate.replace(/-/g, "/")).getTime() + totalTime,
  ).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return endDate;
};

const calculateTotal = (price: number, weeks: number) => {
  return price * weeks;
};

export const OrderConfirmationStep: FunctionComponent<OrderConfirmationStepProps> = ({
  invocations,
  fetchPaymentDetails,
  paymentDetails,
}: OrderConfirmationStepProps) => {
  const [origamiData, setOrigamiData] = useState<OrigamiDataType>(
    defineOrigamiData(invocations),
  );

  useEffect(() => {
    setOrigamiData(defineOrigamiData(invocations));
  }, [setOrigamiData, invocations]);

  useEffect(() => {
    if (!paymentDetails) fetchPaymentDetails();
  }, [fetchPaymentDetails, paymentDetails]);

  const selectedWidget = invocations["widget"] as SelectedWidget;
  const selectedDuration = Number(invocations["duration"]);
  const selectedStartDate = String(invocations["start_date"]);
  const selectedEndDate = calculateEndDate(
    String(invocations["start_date"]),
    selectedDuration,
  );

  let summaryItems: Array<ItemProps> | null = null;

  if (paymentDetails) {
    summaryItems = [
      {
        klass: "line-total",
        label: "Amount Paid",
        value: `$${calculateTotal(selectedWidget?.price, selectedDuration)}`,
      },
      {
        klass: "line-date-paid",
        label: "Date Paid",
        value: dateNormalizer(paymentDetails.created_at),
      },
      {
        klass: "line-payment-method",
        label: "Payment Method",
        value: `${paymentDetails.card_brand.toUpperCase()}-${
          paymentDetails.last_numbers
        }`,
      },
      {
        klass: "line-location",
        label: "Location",
        value: selectedWidget?.label,
      },
      {
        klass: "line-start-date",
        label: "Start Date",
        value: dateNormalizer(selectedStartDate),
      },
      {
        klass: "line-end-date",
        label: "End Date",
        value: dateNormalizer(selectedEndDate),
      },
      {
        klass: "line-rate",
        label: "Rate",
        value: `$${selectedWidget?.price}/week`,
      },
      {
        klass: "line-total-weeks",
        label: "Total Weeks",
        value: selectedDuration,
      },
    ];
  }

  return (
    <div>
      <OrigamiPreview
        spell="order_confirmation"
        origamiData={origamiData}
        selectedTab={Tab.Info}
        showExpanded={false}
      />
      <div className="question">
        {!summaryItems && <Spinner />}
        {summaryItems && (
          <OrderSummary
            title="Your order has been confirmed!"
            description="We'll send you an email shortly with all the details."
            items={summaryItems}
            showFooter={false}
            loading={!selectedWidget}
          />
        )}
      </div>
    </div>
  );
};

export default WizardContainer(OrderConfirmationStep);
